import { useLayoutEffect, useCallback, useRef } from 'react';

/**
 * useAutosizeTextarea — universal native textarea auto-expand (Feb 26, 2026)
 *
 * Drop-in hook that adjusts a textarea's height to fit its content. Works
 * across all mood themes (no theme-specific CSS hacks). No dependency, no
 * library — just `scrollHeight` measurement after the browser lays out.
 *
 * Usage:
 *   const ref = useRef(null);
 *   const recompute = useAutosizeTextarea(ref, value, { minRows: 8, maxRows: 40 });
 *   <Textarea ref={ref} value={value} onChange={...} />
 *
 *   // If you programmatically change the value (e.g., voice transcript
 *   // appended outside React state), call recompute() to re-measure.
 *
 * Feb 27, 2026 perf fix — rAF-throttle the recompute. The prior version
 * ran THREE forced reflows (set height to 0 → read scrollHeight → set new
 * height) on every keystroke synchronously inside useLayoutEffect. As the
 * textarea content grew, each layout calculation took longer, producing
 * the user-reported "extreme typing lag" that worsened with chart length.
 * Coalescing into a single per-frame measurement caps the cost at one
 * layout pass per 16ms regardless of typing speed.
 *
 * Notes:
 *   - Uses `useLayoutEffect` so the height adjusts BEFORE the browser paints,
 *     preventing the flicker you'd see with a regular effect.
 *   - Caps at `maxRows` * lineHeight to avoid pushing the page out of the
 *     viewport on huge dictation sessions.
 *   - Respects `min-h-[...]` Tailwind classes already on the element — we
 *     never shrink below the rendered min-height.
 */
export const useAutosizeTextarea = (
  ref,
  value,
  { minRows = 6, maxRows = 40 } = {}
) => {
  const rafIdRef = useRef(null);

  const measureNow = useCallback(() => {
    const el = ref?.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const minHeight = (minRows * lineHeight) + padY + borderY;
    const maxHeight = (maxRows * lineHeight) + padY + borderY;

    // Reset to allow shrink, then measure
    el.style.height = '0px';
    const target = Math.max(minHeight, Math.min(el.scrollHeight + borderY, maxHeight));
    el.style.height = `${target}px`;
    // If we capped at maxHeight, scroll instead of grow
    el.style.overflowY = el.scrollHeight + borderY > maxHeight ? 'auto' : 'hidden';
  }, [ref, minRows, maxRows]);

  // rAF-throttled recompute — coalesces multiple rapid value changes into a
  // single layout pass per frame.
  const recompute = useCallback(() => {
    if (rafIdRef.current != null) return; // already scheduled
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      measureNow();
    });
  }, [measureNow]);

  useLayoutEffect(() => {
    recompute();
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [value, recompute]);

  // Re-measure on window resize (textarea width can change at responsive breakpoints)
  useLayoutEffect(() => {
    const onResize = () => recompute();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recompute]);

  return recompute;
};

export default useAutosizeTextarea;
